import { useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { performanceMonitor } from '@/shared/lib/monitoring/performanceMetrics';
import { miscKeys } from '@/shared/lib/query/queryKeys';

import { performanceStorage } from '@/entities/performance/api/storage';
import type { PerformanceLogFilter, PerformanceStats } from '@/entities/performance/domain/types';

export const usePerformanceLogs = (filter?: PerformanceLogFilter) => {
  const { data: logs = [], refetch } = useQuery({
    queryKey: miscKeys.performanceLogs(),
    queryFn: () => performanceStorage.getLogs(),
    staleTime: 1000,
    refetchInterval: 5000,
  });

  const filteredLogs = useMemo(() => {
    if (!filter) return logs;

    return logs.filter((log) => {
      if (filter.type !== 'all' && log.type !== filter.type) return false;

      if (filter.startDate && log.timestamp < filter.startDate) return false;
      if (filter.endDate && log.timestamp > filter.endDate) return false;

      if (filter.searchUrl && log.data.url) {
        const url = String(log.data.url).toLowerCase();
        if (!url.includes(filter.searchUrl.toLowerCase())) return false;
      }

      if (filter.showErrorsOnly) {
        if (log.type === 'api_response_time' && log.data.status) {
          const status = String(log.data.status);
          if (!status.startsWith('4') && !status.startsWith('5') && status !== 'error') {
            return false;
          }
        }
      }

      return true;
    });
  }, [logs, filter]);

  const stats = useMemo<PerformanceStats>(() => {
    const apiLogs = logs.filter((log) => log.type === 'api_response_time');
    const retryLogs = logs.filter((log) => log.type === 'connection_reset_retry');
    const slowQueryLogs = logs.filter((log) => log.type === 'slow_query');
    const timeoutLogs = logs.filter((log) => log.type === 'timeout');

    const responseTimeStats = performanceMonitor.getResponseTimeStats();

    const slowQuerySizes = slowQueryLogs
      .map((log) => log.data.responseSize as number)
      .filter((s): s is number => typeof s === 'number' && !isNaN(s));

    return {
      totalLogs: logs.length,
      errors: {
        apiErrorCount: apiLogs.length,
        timeoutCount: timeoutLogs.length,
        connectionResetCount: retryLogs.length,
      },
      successQuery: {
        avgDuration: responseTimeStats.avg,
        minDuration: responseTimeStats.min,
        maxDuration: responseTimeStats.max,
        avgResponseSize: responseTimeStats.avgSize,
      },
      slowQuery: {
        count: slowQueryLogs.length,
        avgResponseSize:
          slowQuerySizes.length > 0 ? Math.round(slowQuerySizes.reduce((a, b) => a + b, 0) / slowQuerySizes.length) : 0,
      },
    };
  }, [logs]);

  return {
    logs: filteredLogs,
    allLogs: logs,
    stats,
    refetch,
  };
};

export const usePerformanceLog = (id: string) => {
  return useQuery({
    queryKey: miscKeys.performanceLog(id),
    queryFn: () => performanceStorage.getLogById(id),
    enabled: !!id,
  });
};

export const useClearLogs = () => {
  const queryClient = useQueryClient();

  const clearLogs = () => {
    performanceStorage.clearLogs();
    queryClient.invalidateQueries({ queryKey: miscKeys.performanceLogs() });
  };

  return { clearLogs };
};
